import { useApolloClient } from '@apollo/client';
import { useCallback, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import { invalidateCacheField } from '@/graphql/client.utils';

import { PaneForm } from '../../../ManageIntegrationDataModal.types';
import { CashflowsImportFormShape } from '../CashflowsImport.types';
import {
  hasSelectedCashflows,
  hasSelectedCashflowWithoutTarget,
  mapCashflowsToCreateInputs,
} from '../CashflowsImport.utils';
import { useCreateImportedCashflowMutation } from '../graphql/CashflowsImport.generated';

export interface CashflowsImportForm extends PaneForm {
  formMethods: UseFormReturn<CashflowsImportFormShape>;
  seed: (values: CashflowsImportFormShape) => void;
}

export function useCashflowsImportForm(
  householdId: string,
  onImported?: () => void
): CashflowsImportForm {
  const apolloClient = useApolloClient();
  const { showFeedback } = useFeedback();
  const successfulCashflowIds = useRef(new Set<string>());
  const formMethods = useForm<CashflowsImportFormShape>({
    defaultValues: { cashflows: {} },
  });
  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = formMethods;

  const seed = useCallback(
    (values: CashflowsImportFormShape) => {
      if (isDirty) return;
      reset({
        cashflows: Object.fromEntries(
          Object.entries(values.cashflows).filter(
            ([id]) => !successfulCashflowIds.current.has(id)
          )
        ),
      });
    },
    [isDirty, reset]
  );

  const [createCashflow] = useCreateImportedCashflowMutation();

  const save = handleSubmit(async (values) => {
    if (!hasSelectedCashflows(values.cashflows)) return true;

    if (hasSelectedCashflowWithoutTarget(values.cashflows)) {
      showFeedback('Select an entity for each cash flow.', {
        variant: 'info-high',
      });
      return false;
    }

    const selectedCashflows = Object.entries(values.cashflows).filter(
      ([, cashflow]) => cashflow.shouldImport
    );
    const results = await Promise.allSettled(
      mapCashflowsToCreateInputs(values, householdId).map((input) =>
        createCashflow({ variables: { input } })
      )
    );
    const failedCashflowIds = new Set<string>();

    // Promise.allSettled preserves input order, so each result matches the
    // selected cash flow at the same index.
    results.forEach((result, index) => {
      const cashflowId = selectedCashflows[index]?.[0];
      if (!cashflowId) return;

      if (result.status === 'rejected') failedCashflowIds.add(cashflowId);
      else successfulCashflowIds.current.add(cashflowId);
    });

    if (results.some((result) => result.status === 'fulfilled')) {
      onImported?.();
    }
    invalidateCacheField('cashflows', apolloClient);
    invalidateCacheField('integrationCashflows', apolloClient);
    reset({
      cashflows: Object.fromEntries(
        Object.entries(values.cashflows).filter(
          ([id]) => !successfulCashflowIds.current.has(id)
        )
      ),
    });

    const firstFailure = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );
    if (firstFailure) {
      const failedCount = failedCashflowIds.size;
      showFeedback(
        `${failedCount} cash flow${failedCount === 1 ? '' : 's'} failed to import. Please try again.`,
        { variant: 'error' }
      );
      throw firstFailure.reason;
    }

    return true;
  });

  return { formMethods, save, seed };
}
